import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, Button, Card, CardBody, Col, CustomInput, Input, Progress, Row } from 'reactstrap';
import { Link, withRouter } from 'react-router-dom';
import AppContext from '../../context/Context';
import {
  alertRouteItems,
  analyticsRouteItems,
  settingsRoutes
} from '../../routes';
import { getItemFromStore, setItemToStore } from '../../helpers/utils';
import useAdminNavigationRoutes from '../../hooks/useAdminNavigationRoutes';
import { filterRoutesByPermissions, hasPermission } from '../../helpers/permissions';
import SettingsUserManagement from './SettingsUserManagement';

const SECTION_META = {
  '/pages/settings': {
    label: 'Sozlamalar markazi',
    title: 'Tizim boshqaruv paneli',
    description: "Interfeys, menyu, psixolog moduli va xavfsizlik bo'limlarini bir joydan boshqaring.",
    icon: 'cog'
  },
  '/pages/settings/general': {
    label: 'Umumiy',
    title: "Asosiy ish muhiti",
    description: "Tizim ko'rinishi, til va hudud kabi kundalik ishga ta'sir qiladigan sozlamalarni shu yerda boshqaring.",
    icon: 'sliders-h'
  },
  '/pages/settings/general/appearance': {
    label: "Tizim ko'rinishi",
    title: "Interfeys ko'rinishi",
    description: "Kunduzgi yoki tungi rejim, sahifa kengligi va ishchi makon presetlarini tanlang.",
    icon: 'moon'
  },
  '/pages/settings/general/locale': {
    label: 'Til va hudud',
    title: 'Lokal sozlamalar',
    description: 'Til, vaqt zonasi, sana ko`rinishi va RTL rejimini moslang.',
    icon: 'globe-americas'
  },
  '/pages/settings/navigation': {
    label: 'Menyu',
    title: 'Navigatsiya boshqaruvi',
    description: "Yuqori, yon va aralash menyu rejimlarini sozlang, foydalanuvchi oqimini boshqaring.",
    icon: 'stream'
  },
  '/pages/settings/navigation/top': {
    label: 'Yuqori menyu',
    title: 'Top menu rejimi',
    description: "Kategoriyalarni bir qatorda ko'rsatadigan keng boshqaruv rejimi.",
    icon: 'align-right'
  },
  '/pages/settings/navigation/vertical': {
    label: 'Yon menyu',
    title: 'Vertical menu rejimi',
    description: "Modullarni chap navigatsiya ichida tartibli ko'rsatish uchun mo'ljallangan rejim.",
    icon: 'align-left'
  },
  '/pages/settings/navigation/combo': {
    label: 'Aralash rejim',
    title: 'Combo navigatsiya',
    description: "Top menu va yon menyu kombinatsiyasini ishlatadigan gibrid rejim.",
    icon: 'stream'
  },
  '/pages/settings/module': {
    label: 'Psixolog moduli',
    title: 'Modul orkestratori',
    description: 'Tahlillar, tekshirish paneli va xavf guruhlari bloklarini zamonaviy boshqaruv formatida ko`ring.',
    icon: 'chart-pie'
  },
  '/pages/settings/module/analytics': {
    label: 'Tahlillar',
    title: 'Analitika tarmog`i',
    description: 'Psixologik testlar oqimi, tahlil sahifalari va foydalanuvchi yo`nalishlari shu bo`limda jamlanadi.',
    icon: 'chart-line'
  },
  '/pages/settings/module/review': {
    label: 'Tekshirish paneli',
    title: 'Review nazorati',
    description: 'Guruhlar kesimi, tekshirish oqimi va nazorat ishlarini boshqarish uchun markaziy panel.',
    icon: 'clipboard-list'
  },
  '/pages/settings/module/alerts': {
    label: 'Xavf guruhlari',
    title: 'Alert monitoring',
    description: "Suitsid, depressiya va boshqa xavf segmentlarini tez ko'rish uchun alert konfiguratsiyasi.",
    icon: 'exclamation-triangle'
  },
  '/pages/settings/security': {
    label: 'Xavfsizlik',
    title: 'Kirish va ruxsatlar',
    description: 'Sessiya holati, rol imkoniyatlari, foydalanuvchilar va modul darajasidagi ruxsatlarni kuzating.',
    icon: 'lock'
  },
  '/pages/settings/security/session': {
    label: 'Sessiya',
    title: 'Faol sessiya nazorati',
    description: 'Bearer token, brauzer muhiti va joriy foydalanuvchi holati bo`yicha nazorat paneli.',
    icon: 'user-check'
  },
  '/pages/settings/security/permissions': {
    label: 'Ruxsatlar',
    title: 'Permission matriksi',
    description: 'Joriy rol va modul bo`yicha imkoniyatlarni aniq kartalarda ko`ring.',
    icon: 'tasks'
  },
  '/pages/settings/security/users': {
    label: 'Foydalanuvchilar',
    title: 'Akkauntlar boshqaruvi',
    description: 'Admin va psixolog akkauntlarini qo`shing, tahrirlang, arxivlang va rollarni nazorat qiling.',
    icon: 'users'
  }
};

const SECTION_ICON_BY_PATH = {
  '/pages/settings/general/appearance': 'moon',
  '/pages/settings/general/locale': 'globe-americas',
  '/pages/settings/navigation/top': 'align-right',
  '/pages/settings/navigation/vertical': 'align-left',
  '/pages/settings/navigation/combo': 'stream',
  '/pages/settings/module/analytics': 'chart-line',
  '/pages/settings/module/review': 'clipboard-list',
  '/pages/settings/module/alerts': 'exclamation-triangle',
  '/pages/settings/security/session': 'user-check',
  '/pages/settings/security/permissions': 'tasks',
  '/pages/settings/security/users': 'users'
};

const LOCALE_OPTIONS = [
  { value: 'uz', label: "O'zbekcha" },
  { value: 'ru', label: 'Русский' },
  { value: 'en', label: 'English' }
];

const TIMEZONE_OPTIONS = [
  { value: 'Asia/Tashkent', label: 'Asia/Tashkent' },
  { value: 'Asia/Almaty', label: 'Asia/Almaty' },
  { value: 'Europe/Moscow', label: 'Europe/Moscow' },
  { value: 'UTC', label: 'UTC' }
];

const DATE_STYLE_OPTIONS = [
  { value: 'full', label: 'To`liq ko`rinish' },
  { value: 'long', label: 'Keng format' },
  { value: 'medium', label: "Standart" },
  { value: 'short', label: 'Qisqa format' }
];

const WEEK_START_OPTIONS = [
  { value: 'monday', label: 'Dushanba' },
  { value: 'sunday', label: 'Yakshanba' },
  { value: 'saturday', label: 'Shanba' }
];

const DENSITY_OPTIONS = [
  { value: 'comfortable', label: 'Comfort', description: 'Keng oraliqlar va qulay o`qish.' },
  { value: 'balanced', label: 'Balanced', description: 'Standart boshqaruv va ixcham kartalar.' },
  { value: 'compact', label: 'Compact', description: 'Ko`proq ma`lumotni bir ekranga sig`diradi.' }
];

const ROLE_CAPABILITY_MATRIX = [
  {
    title: 'Dashboard',
    note: 'Bosh sahifa va umumiy monitoring ko`rsatkichlari.',
    permission: 'padmin.dashboard.view'
  },
  {
    title: 'Tahlillar va natijalar',
    note: 'Diagnostika sahifalari, tekshirish oqimi va natijalar paneli.',
    permission: 'padmin.analytics.view'
  },
  {
    title: 'Xavf guruhlari',
    note: 'Alert yo`nalishlari, suitsid va depressiya segmentlari.',
    permission: 'padmin.alerts.view'
  },
  {
    title: 'Talabalar bazasi',
    note: 'Talabalar ro`yxati va umumiy monitoringga kirish.',
    permission: 'padmin.students.view'
  },
  {
    title: 'Excel import va sinxronizatsiya',
    note: 'Talabalarni Excel orqali yuklash va batch sinxronizatsiya.',
    permission: 'padmin.students.import'
  },
  {
    title: 'Talaba tahriri va arxiv',
    note: 'Tahrirlash, arxivlash va guruh bo`yicha ommaviy boshqaruv.',
    permission: 'padmin.students.archive'
  },
  {
    title: 'Talabani o`chirish',
    note: 'Talaba yozuvini bazadan tozalash vakolati.',
    permission: 'padmin.students.delete'
  },
  {
    title: 'Psixologik pasport boshqaruvi',
    note: 'Pasport ko`rish va tahrirlash bo`yicha vakolat.',
    permission: 'padmin.passport.manage'
  },
  {
    title: 'Test sozlamalari',
    note: 'Custom testlar va diagnostika bo`limlarini boshqarish.',
    permission: 'padmin.tests.manage'
  },
  {
    title: 'Sozlamalar markazi',
    note: 'Rolga mos navigatsiya va xavfsizlik sozlamalariga kirish.',
    permission: 'padmin.settings.view'
  },
  {
    title: 'Foydalanuvchilar boshqaruvi',
    note: 'Admin va psixolog akkauntlarini yaratish, yangilash va arxivlash vakolati.',
    permission: 'padmin.users.manage'
  }
];

const MODULE_OVERVIEW_CARDS = [
  {
    title: 'Tahlillar',
    path: '/pages/settings/module/analytics',
    icon: 'chart-line',
    note: '9 ta asosiy tahlil yo`nalishi'
  },
  {
    title: 'Tekshirish paneli',
    path: '/pages/settings/module/review',
    icon: 'clipboard-list',
    note: 'Guruhlar oqimi va nazorat paneli'
  },
  {
    title: 'Xavf guruhlari',
    path: '/pages/settings/module/alerts',
    icon: 'exclamation-triangle',
    note: '5 ta alert segmenti'
  }
];

const NAV_MODE_META = {
  top: {
    title: 'Yuqori menyu',
    note: 'Kategoriyalar yuqori qatorda ko`rinadi.',
    icon: 'align-right'
  },
  vertical: {
    title: 'Yon menyu',
    note: 'Asosiy navigatsiya chap panelga joylanadi.',
    icon: 'align-left'
  },
  combo: {
    title: 'Aralash rejim',
    note: 'Top menu va sidebar birga ishlaydi.',
    icon: 'stream'
  }
};

const PRESET_WORKSPACES = [
  {
    key: 'focus',
    title: 'Focus workspace',
    note: 'Tungi rejim, combo menyu va keng ish maydoni.',
    icon: 'moon',
    apply: ({ setIsDark, setIsFluid, setNavbarPosition, setIsSidebarCollapsed, setIsRTL }) => {
      setIsDark(true);
      setIsFluid(true);
      setNavbarPosition('combo');
      setIsSidebarCollapsed(false);
      setIsRTL(false);
    }
  },
  {
    key: 'classic',
    title: 'Classic admin',
    note: 'Yorug` fon, yon menyu va standart container.',
    icon: 'sun',
    apply: ({ setIsDark, setIsFluid, setNavbarPosition, setIsSidebarCollapsed, setIsRTL }) => {
      setIsDark(false);
      setIsFluid(false);
      setNavbarPosition('vertical');
      setIsSidebarCollapsed(false);
      setIsRTL(false);
    }
  }
];

const localeMap = {
  uz: 'uz-UZ',
  ru: 'ru-RU',
  en: 'en-US'
};

const collectNodes = (route, trail = []) => {
  const currentNode = { ...route, trail: [...trail, route] };
  const children = (route.children || []).reduce((accumulator, child) => accumulator.concat(collectNodes(child, currentNode.trail)), []);

  return [currentNode, ...children];
};

const isRouteActive = (route, pathname) => pathname === route.to || pathname.indexOf(`${route.to}/`) === 0;

const findActiveNode = (pathname, rootRoute = settingsRoutes) => {
  const nodes = collectNodes(rootRoute).sort((left, right) => right.to.length - left.to.length);
  return nodes.find(node => isRouteActive(node, pathname)) || nodes[0] || { ...rootRoute, children: [] };
};

const renderTree = (items, pathname, level = 0) =>
  items.map(item => {
    const hasChildren = Array.isArray(item.children) && item.children.length > 0;
    const isActive = isRouteActive(item, pathname);

    return (
      <div
        key={item.to}
        className={classNames('med-settings-tree-item', {
          'is-active': isActive,
          'has-children': hasChildren,
          [`is-level-${level}`]: true
        })}
      >
        <Link to={item.to} className="med-settings-tree-link">
          <span>{item.name}</span>
          {hasChildren ? (
            <span className="med-settings-tree-meta">
              <FontAwesomeIcon icon="angle-right" />
            </span>
          ) : null}
        </Link>
        {hasChildren ? <div className="med-settings-tree-children">{renderTree(item.children, pathname, level + 1)}</div> : null}
      </div>
    );
  });

const useStoredSetting = (key, defaultValue) => {
  const [value, setValue] = useState(() => getItemFromStore(key, defaultValue));

  useEffect(() => {
    setItemToStore(key, value);
  }, [key, value]);

  return [value, setValue];
};

const getMetaForNode = node => SECTION_META[node.to] || SECTION_META['/pages/settings'];

const formatLocalePreview = (language, timezone, dateStyle) => {
  try {
    return new Intl.DateTimeFormat(localeMap[language] || 'uz-UZ', {
      dateStyle,
      timeStyle: 'short',
      timeZone: timezone
    }).format(new Date());
  } catch (error) {
    return new Date().toLocaleString();
  }
};

const getMenuChildrenCount = route => (Array.isArray(route.children) ? route.children.length : route.to ? 1 : 0);

const Settings = ({ location }) => {
  const {
    isFluid,
    setIsFluid,
    isRTL,
    setIsRTL,
    isDark,
    setIsDark,
    navbarPosition,
    setNavbarPosition,
    isSidebarCollapsed,
    setIsSidebarCollapsed,
    user,
    setUser,
    token,
    sessionReady
  } = useContext(AppContext);
  const navigationRoutes = useAdminNavigationRoutes();
  const visibleSettingsRoute = useMemo(
    () => ({
      ...settingsRoutes,
      children: filterRoutesByPermissions(settingsRoutes.children || [], user)
    }),
    [user]
  );
  const activeNode = useMemo(() => findActiveNode(location.pathname, visibleSettingsRoute), [location.pathname, visibleSettingsRoute]);
  const childSections = activeNode.children || [];
  const meta = getMetaForNode(activeNode);

  const [language, setLanguage] = useStoredSetting('psi_settings_language', 'uz');
  const [timezone, setTimezone] = useStoredSetting('psi_settings_timezone', 'Asia/Tashkent');
  const [dateStyle, setDateStyle] = useStoredSetting('psi_settings_date_style', 'long');
  const [weekStart, setWeekStart] = useStoredSetting('psi_settings_week_start', 'monday');
  const [density, setDensity] = useStoredSetting('psi_settings_density', 'balanced');
  const [showRoleHints, setShowRoleHints] = useStoredSetting('psi_settings_role_hints', true);

  const currentRoleLabel = user?.role_label || 'Mehmon';
  const localePreview = useMemo(() => formatLocalePreview(language, timezone, dateStyle), [dateStyle, language, timezone]);

  const sectionOverviewCards = useMemo(
    () =>
      (visibleSettingsRoute.children || []).map(section => ({
        title: section.name,
        path: section.to,
        note: `${(section.children || []).length} ta ichki bo'lim`,
        icon: SECTION_ICON_BY_PATH[section.children?.[0]?.to] || SECTION_META[section.to]?.icon || 'cog'
      })),
    [visibleSettingsRoute]
  );

  const menuGroupCards = useMemo(
    () =>
      navigationRoutes.map(route => ({
        title: route.name,
        count: getMenuChildrenCount(route),
        icon: route.icon || 'stream',
        path: route.to || route.children?.[0]?.to || '/'
      })),
    [navigationRoutes]
  );

  const sessionStatusCards = [
    {
      title: 'Sessiya',
      value: sessionReady ? 'Faol' : 'Kutilmoqda',
      note: sessionReady ? 'Frontend auth tayyor' : 'Tekshirilmoqda'
    },
    {
      title: 'Rol',
      value: currentRoleLabel,
      note: user?.full_name || 'Foydalanuvchi aniqlanmagan'
    },
    {
      title: 'Bearer token',
      value: token ? 'Mavjud' : "Yo'q",
      note: token ? 'API auth ishlayapti' : 'Faqat sessiya rejimi'
    },
    {
      title: 'Menyu rejimi',
      value: NAV_MODE_META[navbarPosition]?.title || navbarPosition,
      note: isSidebarCollapsed ? 'Sidebar yig`ilgan' : 'Sidebar ochiq'
    }
  ];

  const analyticsHighlights = analyticsRouteItems.map((item, index) => ({
    ...item,
    badge: index === 0 ? 'Ish oqimi' : index < 4 ? 'Soft skill' : 'Signal'
  }));

  const alertHighlights = [
    { ...alertRouteItems[0], tone: 'danger' },
    { ...alertRouteItems[1], tone: 'danger' },
    { ...alertRouteItems[2], tone: 'warning' },
    { ...alertRouteItems[3], tone: 'warning' },
    { ...alertRouteItems[4], tone: 'danger' }
  ];

  const permissionCards = ROLE_CAPABILITY_MATRIX.map(item => ({
    ...item,
    enabled: hasPermission(user, item.permission)
  }));

  const applyWorkspacePreset = preset => {
    preset.apply({
      setIsDark,
      setIsFluid,
      setNavbarPosition,
      setIsSidebarCollapsed,
      setIsRTL
    });
  };

  const renderSubsectionCards = sections => (
    <Row>
      {sections.map(section => (
        <Col md={6} key={section.to} className="mb-3">
          <Card className="border-0 shadow-sm h-100 med-settings-section-card">
            <CardBody className="med-settings-shell-card">
              <div className="d-flex align-items-start justify-content-between mb-3">
                <div className="med-settings-section-icon">
                  <FontAwesomeIcon icon={SECTION_ICON_BY_PATH[section.to] || 'sliders-h'} />
                </div>
                <Badge color={section.children ? 'soft-primary' : 'soft-info'} pill>
                  {section.children ? `${section.children.length} bo'lim` : 'Tayyor'}
                </Badge>
              </div>
              <h5 className="mb-2">{section.name}</h5>
              <p className="text-500 mb-3">
                {section.children
                  ? `${section.children.length} ta ichki panel bilan boshqariladi.`
                  : "Bu bo'lim to'g'ridan-to'g'ri boshqaruv sahifasiga olib o'tadi."}
              </p>
              <Link to={section.to} className="med-settings-section-link">
                Ochish
                <FontAwesomeIcon icon="arrow-right" className="ml-2" />
              </Link>
            </CardBody>
          </Card>
        </Col>
      ))}
    </Row>
  );

  const renderHero = (extraBadge = null) => (
    <Card className="border-0 shadow-sm mb-3 med-settings-view-hero-card">
      <CardBody className="med-settings-shell-card">
        <div className="med-settings-view-hero">
          <div className="med-settings-view-hero__copy">
            <Badge color="soft-primary" pill className="mb-2">
              {meta.label}
            </Badge>
            <h4 className="mb-2">{meta.title}</h4>
            <p className="text-600 mb-0">{meta.description}</p>
          </div>
          <div className="med-settings-view-hero__aside">
            <div className="med-settings-view-hero__icon">
              <FontAwesomeIcon icon={meta.icon} />
            </div>
            {extraBadge}
          </div>
        </div>
      </CardBody>
    </Card>
  );

  const renderRootOverview = () => (
    <>
      {renderHero(
        <Badge color="soft-info" pill className="mt-3 mt-xl-0">
          {sectionOverviewCards.length} ta asosiy bo'lim
        </Badge>
      )}

      <Row>
        {sectionOverviewCards.map(item => (
          <Col md={6} key={item.path} className="mb-3">
            <Card className="border-0 shadow-sm h-100 med-settings-overview-card">
              <CardBody className="med-settings-shell-card">
                <div className="d-flex align-items-start justify-content-between mb-3">
                  <div className="med-settings-section-icon">
                    <FontAwesomeIcon icon={item.icon} />
                  </div>
                  <Badge color="soft-primary" pill>
                    {item.note}
                  </Badge>
                </div>
                <h5 className="mb-2">{item.title}</h5>
                <p className="text-500 mb-3">
                  {item.title === 'Umumiy'
                    ? "Interfeys ko'rinishi va lokal sozlamalar shu bo'limda boshqariladi."
                    : item.title === 'Menyu'
                    ? "Top menu, sidebar va combo rejimlar shu yerdan boshqariladi."
                    : item.title === 'Psixolog moduli'
                    ? "Analitika, review va alert bloklari uchun alohida nazorat panellari mavjud."
                    : 'Sessiya holati va ruxsatlar bo`yicha nazorat shu yerda jamlanadi.'}
                </p>
                <Link to={item.path} className="med-settings-section-link">
                  Bo'limni ochish
                  <FontAwesomeIcon icon="arrow-right" className="ml-2" />
                </Link>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      <Row>
        {sessionStatusCards.map(card => (
          <Col md={6} xl={3} key={card.title} className="mb-3">
            <div className="med-settings-status-card">
              <span>{card.title}</span>
              <strong>{card.value}</strong>
              <small>{card.note}</small>
            </div>
          </Col>
        ))}
      </Row>
    </>
  );

  const renderGeneralOverview = () => (
    <>
      {renderHero(
        <Badge color="soft-success" pill className="mt-3 mt-xl-0">
          {isDark ? 'Tungi rejim' : 'Kunduzgi rejim'}
        </Badge>
      )}
      <Row>
        <Col lg={7} className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <CardBody className="med-settings-shell-card">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div>
                  <h5 className="mb-1">Ishchi presetlar</h5>
                  <p className="text-500 mb-0">Bir bosishda tavsiya etilgan muhitni qo`llang.</p>
                </div>
                <Badge color="soft-info" pill>
                  2 ta preset
                </Badge>
              </div>
              <div className="med-settings-focus-grid">
                {PRESET_WORKSPACES.map(preset => (
                  <button key={preset.key} type="button" className="med-settings-focus-card" onClick={() => applyWorkspacePreset(preset)}>
                    <div className="med-settings-focus-card__head">
                      <div className="med-settings-section-icon">
                        <FontAwesomeIcon icon={preset.icon} />
                      </div>
                      <FontAwesomeIcon icon="arrow-right" className="text-primary" />
                    </div>
                    <strong>{preset.title}</strong>
                    <span>{preset.note}</span>
                  </button>
                ))}
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col lg={5} className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <CardBody className="med-settings-shell-card">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div>
                  <h5 className="mb-1">Joriy muhit</h5>
                  <p className="text-500 mb-0">Umumiy ishlash holati shu yerda ko`rinadi.</p>
                </div>
                <Badge color="soft-warning" pill>
                  Live
                </Badge>
              </div>
              <div className="med-settings-status-grid">
                <div className="med-settings-status-card">
                  <span>Ko`rinish</span>
                  <strong>{isDark ? 'Dark' : 'Light'}</strong>
                  <small>{density} density</small>
                </div>
                <div className="med-settings-status-card">
                  <span>Layout</span>
                  <strong>{isFluid ? 'Fluid' : 'Container'}</strong>
                  <small>{NAV_MODE_META[navbarPosition]?.title}</small>
                </div>
                <div className="med-settings-status-card">
                  <span>Lokal</span>
                  <strong>{LOCALE_OPTIONS.find(item => item.value === language)?.label}</strong>
                  <small>{timezone}</small>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      {renderSubsectionCards(childSections)}
    </>
  );

  const renderAppearance = () => {
    const themeOptions = [
      { key: 'light', title: 'Kunduzgi', active: !isDark, onClick: () => setIsDark(false), previewClass: 'light' },
      { key: 'dark', title: 'Tungi', active: isDark, onClick: () => setIsDark(true), previewClass: 'dark' }
    ];

    return (
      <>
        {renderHero(
          <Badge color="soft-info" pill className="mt-3 mt-xl-0">
            {isFluid ? 'Keng canvas' : 'Standart container'}
          </Badge>
        )}
        <Row>
          <Col xl={7} className="mb-3">
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="med-settings-shell-card">
                <div className="med-settings-section">
                  <div className="med-settings-section-title">Ko`rinish ranglari</div>
                  <div className="med-settings-section-copy">Tizim uchun kerakli rang palitrasini tanlang.</div>
                  <div className="med-settings-grid med-settings-grid-2">
                    {themeOptions.map(option => (
                      <button
                        key={option.key}
                        type="button"
                        className={classNames('med-preview-card', `med-preview-card-${option.previewClass}`, {
                          active: option.active
                        })}
                        onClick={option.onClick}
                      >
                        <div className="med-preview-frame">
                          <span className="med-preview-sidebar" />
                          <span className="med-preview-header" />
                          <span className="med-preview-chart" />
                          <span className="med-preview-block med-preview-block-a" />
                          <span className="med-preview-block med-preview-block-b" />
                        </div>
                        <div className="med-preview-meta">
                          <span className="med-preview-radio" />
                          {option.title}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="med-settings-section mb-0 pb-0 border-0">
                  <div className="med-settings-section-title">Kontent zichligi</div>
                  <div className="med-settings-section-copy">Kartalar va bloklar qanchalik ixcham ko`rinishini belgilang.</div>
                  <div className="med-settings-focus-grid">
                    {DENSITY_OPTIONS.map(option => (
                      <button
                        key={option.value}
                        type="button"
                        className={classNames('med-settings-focus-card', {
                          'is-active': density === option.value
                        })}
                        onClick={() => setDensity(option.value)}
                      >
                        <strong>{option.label}</strong>
                        <span>{option.description}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col xl={5} className="mb-3">
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="med-settings-shell-card">
                <div className="med-settings-switch-row">
                  <div>
                    <div className="med-settings-switch-title">To`liq joylashish</div>
                    <div className="med-settings-section-copy mb-0">Container va fluid rejim o`rtasida almashadi.</div>
                  </div>
                  <CustomInput type="switch" id="appearance-fluid-switch" checked={isFluid} onChange={({ target }) => setIsFluid(target.checked)} />
                </div>

                <div className="med-settings-switch-row">
                  <div>
                    <div className="med-settings-switch-title">Sidebar yig`ish</div>
                    <div className="med-settings-section-copy mb-0">Desktop ekranda chap menyuni ixcham ko`rinishga o`tkazadi.</div>
                  </div>
                  <CustomInput
                    type="switch"
                    id="appearance-sidebar-switch"
                    checked={isSidebarCollapsed}
                    onChange={({ target }) => setIsSidebarCollapsed(target.checked)}
                    disabled={navbarPosition === 'top'}
                  />
                </div>

                <div className="med-settings-preview-note">
                  <div className="med-settings-preview-note__icon">
                    <FontAwesomeIcon icon="laptop" />
                  </div>
                  <div>
                    <strong>Preview holati</strong>
                    <span>
                      Hozir sahifa ` {isDark ? 'dark' : 'light'} ` temada, ` {isFluid ? 'fluid' : 'container'} ` canvas bilan
                      ochilgan.
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </>
    );
  };

  const renderLocale = () => (
    <>
      {renderHero(
        <Badge color="soft-info" pill className="mt-3 mt-xl-0">
          {LOCALE_OPTIONS.find(item => item.value === language)?.label}
        </Badge>
      )}
      <Row>
        <Col xl={7} className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <CardBody className="med-settings-shell-card">
              <Row>
                <Col md={6} className="mb-3">
                  <div className="med-settings-section-title mb-2">Til</div>
                  <Input type="select" className="med-settings-select" value={language} onChange={({ target }) => setLanguage(target.value)}>
                    {LOCALE_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={6} className="mb-3">
                  <div className="med-settings-section-title mb-2">Vaqt zonasi</div>
                  <Input type="select" className="med-settings-select" value={timezone} onChange={({ target }) => setTimezone(target.value)}>
                    {TIMEZONE_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={6} className="mb-3">
                  <div className="med-settings-section-title mb-2">Sana formati</div>
                  <Input type="select" className="med-settings-select" value={dateStyle} onChange={({ target }) => setDateStyle(target.value)}>
                    {DATE_STYLE_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={6} className="mb-3">
                  <div className="med-settings-section-title mb-2">Hafta boshlanishi</div>
                  <Input type="select" className="med-settings-select" value={weekStart} onChange={({ target }) => setWeekStart(target.value)}>
                    {WEEK_START_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                </Col>
              </Row>

              <div className="med-settings-switch-row mb-0 border-0 pb-0">
                <div>
                  <div className="med-settings-switch-title">RTL rejimi</div>
                  <div className="med-settings-section-copy mb-0">Matn va layout yo`nalishini o`ngdan chapga o`zgartiradi.</div>
                </div>
                <CustomInput type="switch" id="locale-rtl-switch" checked={isRTL} onChange={({ target }) => setIsRTL(target.checked)} />
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col xl={5} className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <CardBody className="med-settings-shell-card">
              <div className="med-settings-locale-preview">
                <Badge color="soft-success" pill className="mb-3">
                  Live preview
                </Badge>
                <h5 className="mb-2">{localePreview}</h5>
                <p className="text-500 mb-3">
                  Til: {LOCALE_OPTIONS.find(item => item.value === language)?.label}, hafta boshlanishi: {WEEK_START_OPTIONS.find(item => item.value === weekStart)?.label}.
                </p>
                <div className="med-settings-chip-list">
                  <span className="med-settings-chip">{timezone}</span>
                  <span className="med-settings-chip">{isRTL ? 'RTL yoqilgan' : 'LTR standart'}</span>
                  <span className="med-settings-chip">{dateStyle} format</span>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );

  const renderNavigationOverview = () => (
    <>
      {renderHero(
        <Badge color="soft-warning" pill className="mt-3 mt-xl-0">
          {NAV_MODE_META[navbarPosition]?.title}
        </Badge>
      )}
      <Row>
        {Object.entries(NAV_MODE_META).map(([key, item]) => (
          <Col md={4} key={key} className="mb-3">
            <button
              type="button"
              className={classNames('med-settings-focus-card w-100 text-left', {
                'is-active': navbarPosition === key
              })}
              onClick={() => setNavbarPosition(key)}
            >
              <div className="med-settings-focus-card__head">
                <div className="med-settings-section-icon">
                  <FontAwesomeIcon icon={item.icon} />
                </div>
                {navbarPosition === key ? <Badge color="soft-success" pill>Faol</Badge> : null}
              </div>
              <strong>{item.title}</strong>
              <span>{item.note}</span>
            </button>
          </Col>
        ))}
      </Row>

      <Card className="border-0 shadow-sm mb-3">
        <CardBody className="med-settings-shell-card">
          <div className="med-settings-switch-row mb-0 border-0 pb-0">
            <div>
              <div className="med-settings-switch-title">Sidebar yig`ish</div>
              <div className="med-settings-section-copy mb-0">Vertical va combo rejimlarida tezkor ixcham ko`rinish.</div>
            </div>
            <CustomInput
              type="switch"
              id="navigation-collapse-switch"
              checked={isSidebarCollapsed}
              onChange={({ target }) => setIsSidebarCollapsed(target.checked)}
              disabled={navbarPosition === 'top'}
            />
          </div>
        </CardBody>
      </Card>

      {renderSubsectionCards(childSections)}
    </>
  );

  const renderMenuRoutes = (title, description, routes, activeColor = 'soft-info') => (
    <Card className="border-0 shadow-sm">
      <CardBody className="med-settings-shell-card">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h5 className="mb-1">{title}</h5>
            <p className="text-500 mb-0">{description}</p>
          </div>
          <Badge color={activeColor} pill>
            {routes.length} ta blok
          </Badge>
        </div>
        <div className="med-settings-route-list">
          {routes.map(route => (
            <div key={route.to || route.name} className="med-settings-route-item">
              <div>
                <strong>{route.name}</strong>
                <span>{route.children ? `${route.children.length} ta ichki yo'nalish` : route.to}</span>
              </div>
              <Link to={route.to || route.children?.[0]?.to || '/'} className="med-settings-section-link">
                Ochish
              </Link>
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  );

  const renderTopNavigation = () => (
    <>
      {renderHero(
        <Button color="primary" size="sm" className="mt-3 mt-xl-0" onClick={() => setNavbarPosition('top')}>
          Faollashtirish
        </Button>
      )}
      <Row>
        <Col xl={5} className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <CardBody className="med-settings-shell-card">
              <div className="med-settings-preview-note">
                <div className="med-settings-preview-note__icon">
                  <FontAwesomeIcon icon="align-right" />
                </div>
                <div>
                  <strong>Top menu ishlash usuli</strong>
                  <span>Asosiy bo`limlar yuqorida ochiladi, ekran keng bo`lganda boshqaruv tezlashadi.</span>
                </div>
              </div>
              <div className="med-settings-chip-list mt-3">
                {navigationRoutes.map(route => (
                  <span key={route.name} className="med-settings-chip">
                    {route.name}
                  </span>
                ))}
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col xl={7} className="mb-3">
          {renderMenuRoutes("Yuqori menyu tarkibi", "Top navigatsiyada ko'rinadigan bloklar va ularning ichki bo'limlari.", navigationRoutes)}
        </Col>
      </Row>
    </>
  );

  const renderVerticalNavigation = () => (
    <>
      {renderHero(
        <Button color="primary" size="sm" className="mt-3 mt-xl-0" onClick={() => setNavbarPosition('vertical')}>
          Faollashtirish
        </Button>
      )}
      <Row>
        <Col xl={5} className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <CardBody className="med-settings-shell-card">
              <div className="med-settings-switch-row">
                <div>
                  <div className="med-settings-switch-title">Chap panelni yig`ish</div>
                  <div className="med-settings-section-copy mb-0">Kichik ekranlarda ko`proq kontent ko`rish uchun.</div>
                </div>
                <CustomInput type="switch" id="vertical-collapse-switch" checked={isSidebarCollapsed} onChange={({ target }) => setIsSidebarCollapsed(target.checked)} />
              </div>
              <div className="med-settings-preview-note">
                <div className="med-settings-preview-note__icon">
                  <FontAwesomeIcon icon="align-left" />
                </div>
                <div>
                  <strong>Sidebar strategiyasi</strong>
                  <span>Analitika, alert va pasport kabi bo`limlar strukturalangan ko`rinishda chap panelda qoladi.</span>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col xl={7} className="mb-3">
          {renderMenuRoutes("Yon menyu guruhlari", "Sidebar ichida ko'rinadigan guruhlar va ulardagi ichki sahifalar.", menuGroupCards)}
        </Col>
      </Row>
    </>
  );

  const renderComboNavigation = () => (
    <>
      {renderHero(
        <Button color="primary" size="sm" className="mt-3 mt-xl-0" onClick={() => setNavbarPosition('combo')}>
          Faollashtirish
        </Button>
      )}
      <Card className="border-0 shadow-sm mb-3">
        <CardBody className="med-settings-shell-card">
          <Row>
            <Col lg={6} className="mb-3 mb-lg-0">
              <div className="med-settings-preview-note h-100">
                <div className="med-settings-preview-note__icon">
                  <FontAwesomeIcon icon="stream" />
                </div>
                <div>
                  <strong>Combo oqim</strong>
                  <span>Top menu orqali yuqori kategoriyalar, sidebar orqali ichki navigatsiya boshqariladi.</span>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="med-settings-chip-list">
                <span className="med-settings-chip">Katta ekranlar uchun mos</span>
                <span className="med-settings-chip">Analytics + boshqaruv paneli</span>
                <span className="med-settings-chip">Gibrid navigatsiya</span>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>
      {renderMenuRoutes('Combo tarkibi', "Top kategoriya va chap ichki bo'limlarni birlashtiradigan tartib.", navigationRoutes, 'soft-warning')}
    </>
  );

  const renderModuleOverview = () => (
    <>
      {renderHero(
        <Badge color="soft-primary" pill className="mt-3 mt-xl-0">
          3 ta yadro blok
        </Badge>
      )}
      <Row>
        {MODULE_OVERVIEW_CARDS.map(card => (
          <Col md={6} xl={4} key={card.path} className="mb-3">
            <Card className="border-0 shadow-sm h-100 med-settings-overview-card">
              <CardBody className="med-settings-shell-card">
                <div className="d-flex align-items-start justify-content-between mb-3">
                  <div className="med-settings-section-icon">
                    <FontAwesomeIcon icon={card.icon} />
                  </div>
                  <Badge color="soft-info" pill>
                    {card.note}
                  </Badge>
                </div>
                <h5 className="mb-2">{card.title}</h5>
                <p className="text-500 mb-3">
                  {card.title === 'Tahlillar'
                    ? "Talabalar test oqimini turli kesimlarda tahlil qiladi."
                    : card.title === 'Tekshirish paneli'
                    ? "Guruhlar va tekshirish holatini nazorat qilish markazi."
                    : "Xavf segmentlarini aniqlash va kuzatish uchun ishlatiladi."}
                </p>
                <Link to={card.path} className="med-settings-section-link">
                  Ochish
                  <FontAwesomeIcon icon="arrow-right" className="ml-2" />
                </Link>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>
    </>
  );

  const renderAnalyticsModule = () => (
    <>
      {renderHero(
        <Badge color="soft-info" pill className="mt-3 mt-xl-0">
          {analyticsRouteItems.length} ta tahlil yo`nalishi
        </Badge>
      )}
      <Card className="border-0 shadow-sm">
        <CardBody className="med-settings-shell-card">
          <div className="med-settings-route-list">
            {analyticsHighlights.map(item => (
              <div key={item.to} className="med-settings-route-item">
                <div>
                  <strong>{item.name}</strong>
                  <span>{item.badge} qatlamida ishlaydi</span>
                </div>
                <div className="d-flex align-items-center">
                  <Badge color="soft-primary" pill className="mr-3">
                    {item.badge}
                  </Badge>
                  <Link to={item.to} className="med-settings-section-link">
                    Ochish
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>
    </>
  );

  const renderReviewModule = () => (
    <>
      {renderHero(
        <Link to="/tekshirish" className="btn btn-primary btn-sm mt-3 mt-xl-0">
          Panelni ochish
        </Link>
      )}
      <Row>
        {[
          { title: 'Guruh oqimi', note: 'Guruh tanlash va tekshirish yo`nalishi', icon: 'users' },
          { title: 'Nazorat nuqtasi', note: 'Muammo topilgan guruhlarni ko`rsatadi', icon: 'check-circle' },
          { title: 'Ish jarayoni', note: 'Tekshirishni bosqichma-bosqich boshqaradi', icon: 'clipboard-list' }
        ].map(item => (
          <Col md={4} key={item.title} className="mb-3">
            <div className="med-settings-focus-card is-active">
              <div className="med-settings-focus-card__head">
                <div className="med-settings-section-icon">
                  <FontAwesomeIcon icon={item.icon} />
                </div>
              </div>
              <strong>{item.title}</strong>
              <span>{item.note}</span>
            </div>
          </Col>
        ))}
      </Row>
    </>
  );

  const renderAlertsModule = () => (
    <>
      {renderHero(
        <Badge color="soft-danger" pill className="mt-3 mt-xl-0">
          {alertRouteItems.length} ta alert segment
        </Badge>
      )}
      <Card className="border-0 shadow-sm">
        <CardBody className="med-settings-shell-card">
          <div className="med-settings-route-list">
            {alertHighlights.map(item => (
              <div key={item.to} className="med-settings-route-item">
                <div>
                  <strong>{item.name}</strong>
                  <span>Xavf guruhlari panelida alohida route sifatida ishlaydi</span>
                </div>
                <div className="d-flex align-items-center">
                  <Badge color={item.tone === 'danger' ? 'soft-danger' : 'soft-warning'} pill className="mr-3">
                    {item.tone === 'danger' ? 'Kritik' : 'Kuzatuv'}
                  </Badge>
                  <Link to={item.to} className="med-settings-section-link">
                    Ochish
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>
    </>
  );

  const renderSecurityOverview = () => (
    <>
      {renderHero(
        <Badge color={token ? 'soft-success' : 'soft-warning'} pill className="mt-3 mt-xl-0">
          {token ? 'API auth faol' : 'Sessiya auth'}
        </Badge>
      )}
      <Row>
        {sessionStatusCards.map(card => (
          <Col md={6} xl={3} key={card.title} className="mb-3">
            <div className="med-settings-status-card">
              <span>{card.title}</span>
              <strong>{card.value}</strong>
              <small>{card.note}</small>
            </div>
          </Col>
        ))}
      </Row>
      {renderSubsectionCards(childSections)}
    </>
  );

  const renderSessionSecurity = () => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000';
    const browserLocale = typeof navigator !== 'undefined' ? navigator.language : 'uz-UZ';
    const tokenShort = token ? `${token.slice(0, 16)}...` : "Token yo'q";

    return (
      <>
        {renderHero(
          <Badge color={sessionReady ? 'soft-success' : 'soft-warning'} pill className="mt-3 mt-xl-0">
            {sessionReady ? 'Sessiya tayyor' : 'Sinxronlash'}
          </Badge>
        )}
        <Row>
          <Col xl={7} className="mb-3">
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="med-settings-shell-card">
                <div className="med-settings-route-list">
                  {[
                    { title: 'Joriy foydalanuvchi', value: user?.full_name || 'Aniqlanmagan', note: currentRoleLabel },
                    { title: 'API origin', value: origin, note: 'Frontend manzili' },
                    { title: 'Brauzer lokal', value: browserLocale, note: 'Client taraf sozlamasi' },
                    { title: 'Token qisqa ko`rinish', value: tokenShort, note: token ? 'Bearer auth ishlayapti' : 'Session cookie rejimi' }
                  ].map(item => (
                    <div key={item.title} className="med-settings-route-item">
                      <div>
                        <strong>{item.title}</strong>
                        <span>{item.note}</span>
                      </div>
                      <Badge color="soft-secondary" pill>
                        {item.value}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col xl={5} className="mb-3">
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="med-settings-shell-card">
                <div className="med-settings-preview-note">
                  <div className="med-settings-preview-note__icon">
                    <FontAwesomeIcon icon="lock" />
                  </div>
                  <div>
                    <strong>Xavfsiz sessiya eslatmasi</strong>
                    <span>Joriy sessiya `auth.php?action=me` orqali tekshiriladi va 401 javobda avtomatik tozalanadi.</span>
                  </div>
                </div>
                <div className="med-settings-switch-row mt-3 mb-0 border-0 pb-0">
                  <div>
                    <div className="med-settings-switch-title">Rol bo`yicha ko`rsatmalar</div>
                    <div className="med-settings-section-copy mb-0">Security bo`limlarida qisqa yo`riqnomalarni ko`rsatadi.</div>
                  </div>
                  <CustomInput type="switch" id="session-role-hint-switch" checked={showRoleHints} onChange={({ target }) => setShowRoleHints(target.checked)} />
                </div>
                {showRoleHints ? (
                  <div className="med-settings-chip-list mt-3">
                    <span className="med-settings-chip">401 da logout</span>
                    <span className="med-settings-chip">Bearer + session</span>
                    <span className="med-settings-chip">Role aware UI</span>
                  </div>
                ) : null}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </>
    );
  };

  const renderPermissionsSecurity = () => (
    <>
      {renderHero(
        <Badge color="soft-info" pill className="mt-3 mt-xl-0">
          {currentRoleLabel}
        </Badge>
      )}
      <Row>
        {permissionCards.map(item => (
          <Col md={6} key={item.title} className="mb-3">
            <Card className={classNames('border-0 shadow-sm h-100', { 'med-settings-overview-card': item.enabled })}>
              <CardBody className="med-settings-shell-card">
                <div className="d-flex align-items-start justify-content-between mb-3">
                  <div className="med-settings-section-icon">
                    <FontAwesomeIcon icon={item.enabled ? 'check-circle' : 'lock'} />
                  </div>
                  <Badge color={item.enabled ? 'soft-success' : 'soft-secondary'} pill>
                    {item.enabled ? 'Ruxsat bor' : "Yopiq"}
                  </Badge>
                </div>
                <h5 className="mb-2">{item.title}</h5>
                <p className="text-500 mb-3">{item.note}</p>
                <div className="med-settings-mini-progress">
                  <Progress value={item.enabled ? 100 : 35} color={item.enabled ? 'success' : 'secondary'} />
                </div>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>
    </>
  );

  const renderUsersSecurity = () => (
    <>
      {renderHero(
        <Badge color="soft-primary" pill className="mt-3 mt-xl-0">
          Akkauntlar nazorati
        </Badge>
      )}
      <SettingsUserManagement currentUser={user} onCurrentUserUpdate={setUser} />
    </>
  );

  const renderContent = () => {
    switch (activeNode.to) {
      case '/pages/settings':
        return renderRootOverview();
      case '/pages/settings/general':
        return renderGeneralOverview();
      case '/pages/settings/general/appearance':
        return renderAppearance();
      case '/pages/settings/general/locale':
        return renderLocale();
      case '/pages/settings/navigation':
        return renderNavigationOverview();
      case '/pages/settings/navigation/top':
        return renderTopNavigation();
      case '/pages/settings/navigation/vertical':
        return renderVerticalNavigation();
      case '/pages/settings/navigation/combo':
        return renderComboNavigation();
      case '/pages/settings/module':
        return renderModuleOverview();
      case '/pages/settings/module/analytics':
        return renderAnalyticsModule();
      case '/pages/settings/module/review':
        return renderReviewModule();
      case '/pages/settings/module/alerts':
        return renderAlertsModule();
      case '/pages/settings/security':
        return renderSecurityOverview();
      case '/pages/settings/security/session':
        return renderSessionSecurity();
      case '/pages/settings/security/permissions':
        return renderPermissionsSecurity();
      case '/pages/settings/security/users':
        return renderUsersSecurity();
      default:
        return renderRootOverview();
    }
  };

  return (
    <Row>
      <Col xl={4} className="mb-3">
        <Card className="border-0 shadow-sm h-100">
          <CardBody className="med-settings-shell-card">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div>
                <Badge color="soft-primary" pill>
                  Sozlamalar
                </Badge>
                <h5 className="mb-1 mt-2">Bo'limlar daraxti</h5>
                <p className="text-500 mb-0">Rasmdagi kabi ichki bo'limlar shu yerda ko'rinadi va to'g'ridan-to'g'ri ishlaydi.</p>
              </div>
              <div className="med-settings-shell-icon">
                <FontAwesomeIcon icon="cog" />
              </div>
            </div>

            <div className="med-settings-tree">{renderTree(visibleSettingsRoute.children || [], location.pathname)}</div>
          </CardBody>
        </Card>
      </Col>

      <Col xl={8}>{renderContent()}</Col>
    </Row>
  );
};

Settings.propTypes = {
  location: PropTypes.object.isRequired
};

export default withRouter(Settings);
